import { FileEdit, Minus, Plus, Save, Trash2 } from "lucide-react";
import React, { useState } from "react";
import Select from "react-select";
import { listStyleOptions } from "../../constants/blog";
import { useFormik } from "formik";
import { listInitialValues, listValidationSchema } from "./validationSchema";
import { SubSection } from "context/BlogContext";
import DeleteModal from "Common/DeleteModal";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

interface ListProps {
  sectionOrder: number;
  subSectionOrder: number;
  handleSaveSubSection: (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => void;
  handleEditSubSection: (sectionOrder: number, subSection: SubSection) => void;
  subSection: SubSection;
  handleDeleteSubSection: (subsectionOrder: number) => void;
}

const List: React.FC<ListProps> = ({
  sectionOrder,
  subSectionOrder,
  handleSaveSubSection,
  handleEditSubSection,
  subSection,
  handleDeleteSubSection,
}) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isCancel, setIsCancel] = useState(false);

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation = useFormik({
    initialValues: listInitialValues(subSection),
    validationSchema: listValidationSchema,
    onSubmit: (values) => {
      handleSaveSubSection(sectionOrder, {
        title: values.title,
        type: "list",
        attrs: { style: values.style, items: values.items },
        isEditing: false,
        order: subSectionOrder,
      });
    },
  });

  const handleAddInput = () => {
    validation.setFieldValue("items", [...validation.values.items, ""]);
  };

  const handleRemoveInput = (index: number) => {
    const updatedItems = validation.values.items.filter(
      (_: number, i: number) => i !== index
    );
    validation.setFieldValue("items", updatedItems);
  };

  const handleTypeChange = (option: any) => {
    validation.setFieldValue("style", option.value);
  };

  // Handle delete confirmation
  const handleDelete = () => {
    handleDeleteSubSection(subSection.order);
    setDeleteModal(false);
  };

  const handleCancelSubSection = () => {
    handleSaveSubSection(sectionOrder, {
      ...subSection,
      isEditing: true,
      // isEditing: subSection.isEditing ? false : true,
    });
    validation.resetForm();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  return (
    <div>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        message="Are you sure you want to remove this subsection?"
      />
      {subSection.isEditing ? (
        <form>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12 pt-5">
            <div className="xl:col-span-6">
              <label
                htmlFor={`subTitle`}
                className="inline-block mb-2 text-base font-medium"
              >
                Title
              </label>
              <input
                type="text"
                id="title"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Title"
                name="title"
                onChange={validation.handleChange} // Bind Formik onChange
                value={validation.values.title} // Bind Formik value
              />
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor={`style`}
                className="inline-block mb-2 text-base font-medium"
              >
                Style
              </label>
              <Select
                className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={listStyleOptions}
                onChange={handleTypeChange}
              />
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor={`items`}
                className="inline-block mb-2 text-base font-medium"
              >
                Items
              </label>
              {validation.values.items.map((input: string, index: number) => (
                <div
                  key={index}
                  className={`gap-x-2.5 ${
                    index > 0 ? "pt-2" : ""
                  } flex text-center border rounded input-step border-slate-200 dark:border-zink-500`}
                >
                  <input
                    type="text"
                    id={`item-${index}`}
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Add Item"
                    name={`items[${index}]`}
                    value={validation.values.items[index]}
                    onChange={validation.handleChange}
                  />
                  <div className="gap-x-2.5 flex items-center">
                    {validation.values.items.length > 1 && (
                      <button
                        type="button"
                        className="border h-[30px] w-[30px] leading-[15px] minusBtn bg-slate-200 dark:bg-zink-600 dark:border-zink-600 rounded transition-all duration-200 ease-linear border-slate-200 text-slate-500 dark:text-zink-200 hover:bg-custom-500 dark:hover:bg-custom-500 hover:text-custom-50 dark:hover:text-custom-50 hover:border-custom-500 dark:hover:border-custom-500 focus:bg-custom-500 dark:focus:bg-custom-500 focus:border-custom-500 dark:focus:border-custom-500 focus:text-custom-50 dark:focus:text-custom-50"
                        onClick={() => handleRemoveInput(index)}
                      >
                        <Minus className="inline-block size-4"></Minus>
                      </button>
                    )}
                    {index === 0 && (
                      <button
                        type="button"
                        className="transition-all h-[30px] w-[30px] duration-200 ease-linear border rounded border-slate-200 bg-slate-200 dark:bg-zink-600 dark:border-zink-600 w-7 plusBtn text-slate-500 dark:text-zink-200 hover:bg-custom-500 dark:hover:bg-custom-500 hover:text-custom-50 dark:hover:text-custom-50 hover:border-custom-500 dark:hover:border-custom-500 focus:bg-custom-500 dark:focus:bg-custom-500 focus:border-custom-500 dark:focus:border-custom-500 focus:text-custom-50 dark:focus:text-custom-50"
                        onClick={handleAddInput}
                      >
                        <Plus className="inline-block size-4"></Plus>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>
      ) : (
        <div className="w-full">
          {subSection.title && <p className="pt-3">{subSection.title}</p>}
          <p className="">
            {subSection.attrs?.style === "ordered" ? (
              <ul className="list-decimal list-inside rounded-md">
                {subSection.attrs.items.map((item: string, index: number) => (
                  <li className={`${index !== 0 ? "pt-2" : ""}`} key={index}>{item}</li>
                ))}
              </ul>
            ) : (
              <ul className="list-disc list-inside rounded-md">
                {subSection.attrs.items.map((item: string, index: number) => (
                  <li className={`${index !== 0 ? "pt-2" : ""}`} key={index}>{item}</li>
                ))}
              </ul>
            )}
          </p>
        </div>
      )}
      <div className="flex justify-end gap-x-2.5 pt-5">
        {subSection.isEditing ? (
          <div className="flex gap-x-3">
            {isCancel && (
              <button
                type="button"
                className={`${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } text-red-500`}
                onClick={() => handleCancelSubSection()}
                disabled={isLoading}
              >
                <div className="flex align-center">
                  <span className="ml-1">Cancel</span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`text-green-500 ${
                isLoading ? "cursor-not-allowed opacity-50" : "hover:bg-green-600 hover:text-white"
              } bg-green-100 btn focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20`}
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <Save className="size-4"></Save>
                <span className="ml-1">Save</span>
              </div>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`text-custom-500 ${
              isLoading ? "cursor-not-allowed opacity-50" : "hover:text-whitehover:bg-custom-600"
            } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
            onClick={() => {
              handleEditSubSection(sectionOrder, subSection);
              setIsCancel(true);
            }} // Switch to edit mode when clicked
            disabled={isLoading}
          >
            <div className="flex align-center">
              <FileEdit className="size-4"></FileEdit>
              <span className="ml-1">Edit</span>
            </div>
          </button>
        )}
        <button
          type="button"
          className={`${
            isLoading ? "cursor-not-allowed opacity-50" : ""
          } text-red-500`}
          onClick={() => setDeleteModal(true)}
          disabled={isLoading}
        >
          <div className="flex align-center">
            <Trash2 className="size-4 text-red-500"></Trash2>
            <span className="ml-1">Delete</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default List;
